/**
 * Dialog criar/editar de atividade agendada — sempre cross-mesa: o select de
 * mesa fica no próprio form. POST aninhado em /desks/:deskId; o PUT leva
 * deskId no corpo (na edição a mesa PODE mudar — o servidor valida).
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Play } from 'lucide-react';
import type {
  DeskDetail,
  RecurrenceKind,
  ScheduledActivity,
  ScheduledActivityBody,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Dialog, Skeleton } from '@/components/ui/misc';
import {
  clientsLookupOptions,
  deskDetailOptions,
  desksListOptions,
  errorMessage,
} from '@/features/desks/desks-queries';
import { minuteToHHMM } from '@/features/desks/desk-time';
import { teamListQuery } from '@/features/team/team-queries';
import { TeamMemberPicker } from '@/features/team/TeamMemberPicker';
import { ClientPicker, type ClientRef } from '@/features/tickets/ClientPicker';
import { CatalogItemPicker, useDeskCatalogs } from '@/features/tickets/CatalogItemPicker';

const WEEKDAY_LABELS = [
  'domingo',
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado',
];

/** "Diária às 08:00" / "Semanal (segunda-feira) às 08:00" / "Mensal (dia 5) às 08:00". */
export function humanizeRecurrence(a: {
  recurrence: RecurrenceKind;
  weekday: number | null;
  monthDay: number | null;
  timeMinute: number;
}): string {
  const time = minuteToHHMM(a.timeMinute);
  switch (a.recurrence) {
    case 'daily':
      return `Diária às ${time}`;
    case 'weekly':
      return `Semanal (${WEEKDAY_LABELS[a.weekday ?? 0]}) às ${time}`;
    case 'monthly':
      return `Mensal (dia ${a.monthDay ?? 1}) às ${time}`;
  }
}

/** Corpo completo do PUT a partir da atividade (toggle de ativo na lista). */
export function scheduledBodyFrom(a: ScheduledActivity, active: boolean): ScheduledActivityBody {
  return {
    name: a.name,
    active,
    ticketTitle: a.ticketTitle,
    ticketDescription: a.ticketDescription,
    clientId: a.clientId,
    priorityId: a.priorityId,
    catalogItemId: a.catalogItemId,
    responsibleId: a.responsibleId,
    recurrence: a.recurrence,
    weekday: a.weekday,
    monthDay: a.monthDay,
    timeMinute: a.timeMinute,
  };
}

export function ScheduledDialog({
  activity,
  onClose,
}: {
  activity: ScheduledActivity | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const clients = useQuery(clientsLookupOptions());
  // o nome do responsável já gravado (a atividade só guarda o id) — a equipe
  // inteira, inativos inclusive: quem saiu de cena continua com nome na tela
  const team = useQuery(teamListQuery({ includeInactive: true }));

  // ── Mesa: obrigatória; muda também na edição (o PUT leva deskId) ──────────
  const [deskId, setDeskId] = useState<number | null>(activity?.deskId ?? null);
  const desksList = useQuery(desksListOptions());
  const deskDetail = useQuery({
    ...deskDetailOptions(deskId ?? 0),
    enabled: deskId !== null,
  });
  const effectiveDesk: DeskDetail | null = deskId !== null ? (deskDetail.data ?? null) : null;
  // Configuração da mesa em voo (ou com erro): sem ela o select de prioridade
  // fingiria mesa sem configuração.
  const deskLoading = deskId !== null && deskDetail.isPending;
  const deskFailed = deskId !== null && deskDetail.isError;

  const deskCatalogsQ = useDeskCatalogs(deskId ?? undefined);
  const deskHasCatalogs = (deskCatalogsQ.data?.length ?? 0) > 0;

  const [name, setName] = useState(activity?.name ?? '');
  const [ticketTitle, setTicketTitle] = useState(activity?.ticketTitle ?? '');
  const [ticketDescription, setTicketDescription] = useState(activity?.ticketDescription ?? '');
  // Prefill do cliente na edição: a atividade só tem o id — o nome vem do lookup.
  const [client, setClient] = useState<ClientRef | null>(() => {
    if (!activity) return null;
    return { id: activity.clientId, name: `Cliente #${activity.clientId}` };
  });
  const [priorityId, setPriorityId] = useState(
    activity?.priorityId != null ? String(activity.priorityId) : ''
  );
  const [catalogItemId, setCatalogItemId] = useState<number | null>(
    activity?.catalogItemId ?? null
  );
  const [responsible, setResponsible] = useState<{ id: number; name: string | null } | null>(
    activity?.responsibleId != null ? { id: activity.responsibleId, name: null } : null
  );
  const [recurrence, setRecurrence] = useState<RecurrenceKind>(activity?.recurrence ?? 'daily');
  const [weekday, setWeekday] = useState(String(activity?.weekday ?? 1));
  const [monthDay, setMonthDay] = useState(String(activity?.monthDay ?? 1));
  const [time, setTime] = useState(
    activity ? minuteToHHMM(activity.timeMinute) : '08:00'
  );
  const [error, setError] = useState<string | null>(null);
  const [runFeedback, setRunFeedback] = useState<string | null>(null);

  // Nome real do cliente quando o lookup carrega (edição).
  const resolvedClient =
    client && clients.data
      ? { id: client.id, name: clients.data.find((c) => c.id === client.id)?.name ?? client.name }
      : client;

  // prefixo largo: cobre a lista cross-mesa e caches por mesa
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['erp', 'scheduled'] });

  const save = useMutation({
    mutationFn: async (body: ScheduledActivityBody) => {
      const res = activity
        ? await api<ScheduledActivity>(`/scheduled-activities/${activity.id}`, {
            method: 'PUT',
            // deskId no corpo: definido muda a mesa (o servidor valida que
            // prioridade/catálogo pertencem à mesa nova e reposiciona)
            body: { ...body, deskId },
          })
        : await api<ScheduledActivity>(`/desks/${deskId}/scheduled-activities`, {
            method: 'POST',
            body,
          });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const run = useMutation({
    mutationFn: async () => {
      if (!activity) return;
      const res = await api<unknown>(`/scheduled-activities/${activity.id}/run`, {
        method: 'POST',
      });
      if (res.error) throw res.error;
    },
    onSuccess: () => {
      setRunFeedback('Disparo executado — o ticket foi criado.');
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['erp', 'tickets'] });
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const parseTime = (value: string): number | null => {
    const match = /^(\d{2}):(\d{2})$/.exec(value);
    if (!match) return null;
    const minute = Number(match[1]) * 60 + Number(match[2]);
    return minute >= 0 && minute <= 1439 ? minute : null;
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !ticketTitle.trim() || deskLoading || deskFailed) return;
    if (deskId === null) {
      setError('Selecione a mesa da atividade.');
      return;
    }
    if (!resolvedClient) {
      setError('Selecione o cliente do ticket.');
      return;
    }
    const timeMinute = parseTime(time);
    if (timeMinute === null) {
      setError('Informe o horário no formato hh:mm.');
      return;
    }
    save.mutate({
      name: name.trim(),
      active: activity?.active ?? true,
      ticketTitle: ticketTitle.trim(),
      ticketDescription: ticketDescription.trim() ? ticketDescription : null,
      clientId: resolvedClient.id,
      priorityId: priorityId === '' ? null : Number(priorityId),
      catalogItemId,
      responsibleId: responsible?.id ?? null,
      recurrence,
      weekday: recurrence === 'weekly' ? Number(weekday) : null,
      monthDay: recurrence === 'monthly' ? Number(monthDay) : null,
      timeMinute,
    });
  };

  const priorities = [...(effectiveDesk?.priorities ?? [])].sort((a, b) => a.position - b.position);
  const responsibleValue = responsible
    ? {
        id: responsible.id,
        name:
          responsible.name ??
          team.data?.items.find((m) => m.id === responsible.id)?.name ??
          (team.isPending ? '…' : `Usuário #${responsible.id}`),
      }
    : null;

  /** Troca de mesa: prioridade e item de catálogo são por mesa. */
  const changeDesk = (value: string) => {
    const next = Number(value);
    if (next === deskId) return;
    setDeskId(next);
    setPriorityId('');
    setCatalogItemId(null);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={activity ? 'Editar atividade agendada' : 'Nova atividade agendada'}
      className="sm:max-w-2xl"
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="sched-desk">
            Mesa <span className="text-destructive">*</span>
          </Label>
          <Select
            id="sched-desk"
            value={deskId === null ? '' : String(deskId)}
            onChange={(e) => changeDesk(e.target.value)}
            required
          >
            <option value="" disabled>
              Selecione a mesa
            </option>
            {[...(desksList.data?.items ?? [])]
              .sort((a, b) => a.position - b.position)
              .map((d) => (
                <option key={d.id} value={d.id}>
                  {d.displayName}
                </option>
              ))}
          </Select>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="sched-name">
              Nome <span className="text-destructive">*</span>
            </Label>
            <Input
              id="sched-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sched-title">
              Título do ticket <span className="text-destructive">*</span>
            </Label>
            <Input
              id="sched-title"
              value={ticketTitle}
              onChange={(e) => setTicketTitle(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="sched-description">Descrição do ticket</Label>
          <Textarea
            id="sched-description"
            value={ticketDescription}
            onChange={(e) => setTicketDescription(e.target.value)}
            rows={3}
            placeholder="Descrição pré-preenchida nos tickets criados…"
          />
        </div>

        <div className="space-y-1.5">
          <Label>
            Cliente <span className="text-destructive">*</span>
          </Label>
          <ClientPicker selected={resolvedClient} onSelect={setClient} />
        </div>

        {deskLoading ? (
          // Configuração da mesa em voo: sem select de prioridade vazio
          // fingindo mesa sem configuração — skeleton e salvar desabilitado.
          <div className="grid gap-3 sm:grid-cols-2" aria-busy>
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : deskFailed ? (
          <p className="rounded-md border border-divider p-3 text-sm text-destructive">
            Erro ao carregar a configuração da mesa: {errorMessage(deskDetail.error)}
          </p>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="sched-priority">Prioridade</Label>
                <Select
                  id="sched-priority"
                  value={priorityId}
                  onChange={(e) => setPriorityId(e.target.value)}
                >
                  <option value="">Sem prioridade</option>
                  {priorities.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                {/* só a equipe deste tenant, com a UX do técnico (pedido da PoolTrack, 05/10) */}
                <Label>Responsável</Label>
                <TeamMemberPicker
                  value={responsibleValue}
                  onChange={(_id, item) =>
                    setResponsible(item ? { id: item.id, name: item.name } : null)
                  }
                />
              </div>
            </div>

            {deskId !== null && deskHasCatalogs ? (
              <div className="space-y-1.5">
                <Label>Item de catálogo</Label>
                <CatalogItemPicker
                  deskId={deskId}
                  value={catalogItemId}
                  onChange={setCatalogItemId}
                  className="grid grid-cols-1 gap-2 space-y-0 sm:grid-cols-3"
                />
              </div>
            ) : null}
          </>
        )}

        <fieldset className="space-y-3 rounded-md border border-divider p-3">
          <legend className="px-1 text-sm font-semibold">Recorrência</legend>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="sched-recurrence">
                Frequência <span className="text-destructive">*</span>
              </Label>
              <Select
                id="sched-recurrence"
                value={recurrence}
                onChange={(e) => setRecurrence(e.target.value as RecurrenceKind)}
              >
                <option value="daily">Diária</option>
                <option value="weekly">Semanal</option>
                <option value="monthly">Mensal</option>
              </Select>
            </div>
            {recurrence === 'weekly' ? (
              <div className="space-y-1.5">
                <Label htmlFor="sched-weekday">
                  Dia da semana <span className="text-destructive">*</span>
                </Label>
                <Select
                  id="sched-weekday"
                  value={weekday}
                  onChange={(e) => setWeekday(e.target.value)}
                >
                  {WEEKDAY_LABELS.map((label, i) => (
                    <option key={i} value={i}>
                      {label.charAt(0).toUpperCase() + label.slice(1)}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}
            {recurrence === 'monthly' ? (
              <div className="space-y-1.5">
                <Label htmlFor="sched-monthday">
                  Dia do mês <span className="text-destructive">*</span>
                </Label>
                <Select
                  id="sched-monthday"
                  value={monthDay}
                  onChange={(e) => setMonthDay(e.target.value)}
                >
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d}>
                      Dia {d}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}
            <div className="space-y-1.5">
              <Label htmlFor="sched-time">
                Horário <span className="text-destructive">*</span>
              </Label>
              <Input
                id="sched-time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                required
              />
            </div>
          </div>
        </fieldset>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {runFeedback ? <p className="text-sm text-success">{runFeedback}</p> : null}
        <div className="flex items-center justify-between gap-2 pt-1">
          <div>
            {activity ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={run.isPending}
                onClick={() => {
                  setError(null);
                  setRunFeedback(null);
                  run.mutate();
                }}
              >
                <Play className="size-3.5" />
                {run.isPending ? 'Executando…' : 'Executar agora'}
              </Button>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={
                save.isPending || !name.trim() || !ticketTitle.trim() || deskLoading || deskFailed
              }
            >
              {save.isPending ? 'Salvando…' : 'Salvar'}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
